import { useQuery } from "@apollo/client/react";
import { DocumentNode } from "@apollo/client";
import {
  GQLQuery,
  GQLQueryOrderInput,
  GQLQueryOrderValue,
} from "@rx-cs-system/schema";
import { useState, useCallback, useEffect } from "react";
import { PAGINATION_LIMIT } from "../settings";
import { useHandleError } from "./useHandleError";
import {
  GET_DELIVERY_ORDERS,
  GET_DELIVERY_ORDERS_WITH_KEYWORDS,
} from "../graphql/queries";

type DataName =
  | "medicalInstitutions"
  | "scUsers"
  | "deliveryOrders"
  | "htUsers"
  | "deliveryOrdersWithKeywords"
  | "haudyUsers";

export const useListDataWithLoadMore = (
  query: DocumentNode,
  dataName: DataName,
  dataNotFoundMessage: string,
) => {
  const [keyword, setKeyword] = useState("");
  // NOTE:患者一覧画面においてデフォルトでは処方日の降順でソートする
  let defaultOrder: GQLQueryOrderInput | undefined;
  switch (dataName) {
    case "scUsers":
      defaultOrder = {
        field: "prescribedAt",
        value: GQLQueryOrderValue.Desc,
      };
      break;
    case "htUsers":
      defaultOrder = {
        field: "usageRecord.createdAt",
        value: GQLQueryOrderValue.Desc,
      };
      break;
    case "haudyUsers":
      defaultOrder = {
        field: "prescribedAt",
        value: GQLQueryOrderValue.Desc,
      };
  }
  const [order, setOrder] = useState<GQLQueryOrderInput | undefined>(
    defaultOrder,
  );
  const [skip, setSkip] = useState(0);

  const { previousData, data, error, loading } = useQuery<GQLQuery>(query, {
    variables: { keyword, order, take: PAGINATION_LIMIT, skip },
  });

  useEffect(() => {
    // check if there is no incoming data
    if (
      !loading &&
      skip !== 0 &&
      previousData?.[dataName] != null &&
      previousData?.[dataName].length === data?.[dataName]?.length
    ) {
      // eslint-disable-next-line no-alert
      alert(dataNotFoundMessage);
    }
  }, [data, dataName, dataNotFoundMessage, previousData, skip, loading]);

  useHandleError(error);

  const onSearchClick = useCallback((input: string) => {
    setSkip(0);
    setKeyword(input);
  }, []);

  const onResetClick = useCallback(() => {
    setSkip(0);
    setKeyword("");
  }, []);

  const onSortClick = useCallback((input: GQLQueryOrderInput) => {
    setOrder(input);
    setSkip(0);
  }, []);

  const onLoadMoreClick = useCallback(
    (newSkip: number) => {
      // when user click on 「さらに表示」button after alert has been show
      // skip state doesn't change
      // → API will not being called again
      // → onCompleted not being called
      // → alert not show
      // → we need to compare skip and show alert in this case
      if (newSkip === skip) {
        // eslint-disable-next-line no-alert
        alert(dataNotFoundMessage);
      } else {
        setSkip(newSkip);
      }
    },
    [skip, dataNotFoundMessage],
  );

  return {
    data,
    variables: {
      keyword,
      order,
      skip,
    },
    handlers: {
      onSearchClick,
      onResetClick,
      onSortClick,
      onLoadMoreClick,
    },
  };
};

export const useListDataWithLoadMoreForDeliveryOrder = () => {
  const [keywords, setKeywords] = useState("");
  const [order, setOrder] = useState<GQLQueryOrderInput>();
  const [offset, setOffset] = useState(0);

  const dataNotFoundMessage = "物品情報は全て表示されています";
  let variables;
  let query;
  let dataName: DataName;
  if (keywords !== "") {
    variables = { keywords, order, limit: PAGINATION_LIMIT, offset };
    query = GET_DELIVERY_ORDERS_WITH_KEYWORDS;
    dataName = "deliveryOrdersWithKeywords";
  } else {
    variables = { order, limit: PAGINATION_LIMIT, offset };
    query = GET_DELIVERY_ORDERS;
    dataName = "deliveryOrders";
  }

  const { previousData, data, error, loading } = useQuery<GQLQuery>(query, {
    variables: variables,
  });

  useEffect(() => {
    // check if there is no incoming data
    if (
      !loading &&
      offset !== 0 &&
      previousData?.[dataName].length === data?.[dataName].length
    ) {
      // eslint-disable-next-line no-alert
      alert(dataNotFoundMessage);
    }
  }, [data, dataName, dataNotFoundMessage, previousData, offset, loading]);

  useHandleError(error);

  const onSearchClick = useCallback((input: string) => {
    setOrder(undefined);
    setOffset(0);
    setKeywords(input);
  }, []);

  const onResetClick = useCallback(() => {
    setOffset(0);
    setKeywords("");
  }, []);

  const onSortClick = useCallback((input: GQLQueryOrderInput) => {
    setOrder(input);
    setOffset(0);
  }, []);

  const onLoadMoreClick = useCallback(
    (newOffset: number) => {
      // when user click on 「さらに表示」button after alert has been show
      // skip state doesn't change
      // → API will not being called again
      // → onCompleted not being called
      // → alert not show
      // → we need to compare skip and show alert in this case
      if (newOffset === offset) {
        // eslint-disable-next-line no-alert
        alert(dataNotFoundMessage);
      } else {
        setOffset(newOffset);
      }
    },
    [offset, dataNotFoundMessage],
  );

  return {
    data,
    variables: {
      keywords,
      order,
      offset,
    },
    handlers: {
      onSearchClick,
      onResetClick,
      onSortClick,
      onLoadMoreClick,
    },
  };
};
